import { useState } from "react";
import { useMutation } from "@apollo/client";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import AvatarDetails from "@/components/AvatarDetails";
import {
  UPSERT_FACILITY_ROLE_MUTATION,
  UpsertFacilityRoleResponse,
  UpsertFacilityRoleVariables
} from "@/pages/facilities/[facilityId]/jobs/(tabbed)/permissions/mutations";
import { showSnackbar } from "@/lib/snackbarUtils";
import classNames from "classnames";
import Banner from "@/components/Banner";

interface AddEmployerRoleModalProps {
  open: boolean;
  onClose: () => void;
  employer: {
    id: string;
    firstName: string;
    lastName: string;
    displayName?: string;
    profilePhoto?: string;
    email?: string;
    primaryFacility?: {
      name: string;
    };
  };
  facilityId: string;
  onSuccess: () => void;
}

export function AddEmployerRoleModal({ open, onClose, employer, facilityId, onSuccess }: AddEmployerRoleModalProps) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [upsertFacilityRole, { loading: isSubmitting }] = useMutation<
    UpsertFacilityRoleResponse,
    UpsertFacilityRoleVariables
  >(UPSERT_FACILITY_ROLE_MUTATION, {
    onCompleted: (data) => {
      if (data?.upsertFacilityRole?.success) {
        showSnackbar(data.upsertFacilityRole.message || "Role added");
        onSuccess();
        onClose();
      } else {
        setErrorMessage(data?.upsertFacilityRole?.message || "Failed to add role");
      }
    },
    onError: (error) => {
      setErrorMessage(error.message || "Failed to add role");
    }
  });

  const handleSave = async () => {
    setErrorMessage(null);
    try {
      await upsertFacilityRole({
        variables: {
          contactId: employer.id,
          facilityId: facilityId,
          role: "Manage Jobs"
        }
      });
    } catch (error) {
      console.error("Error adding employer role:", error);
    }
  };

  const handleClose = () => {
    setErrorMessage(null);
    onClose();
  };

  const displayName = employer.displayName || `${employer.firstName} ${employer.lastName}`;

  return (
    <Dialog
      open={open}
      onClosed={handleClose}
      headline="Add Employer Role"
      className="sm:max-w-[480px]"
      actions={
        <>
          <Button variant="text" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSubmitting}
            icon={isSubmitting ? "progress_activity" : undefined}
            iconClassName={classNames(isSubmitting && "animate-spin w-6 h-6 !block")}
          >
            {isSubmitting ? "Adding..." : "Add Role"}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <p className="text-body-medium text-sys-on-surface">
          Add the &quot;Manage Jobs&quot; role to this person at the selected facility.
        </p>

        {errorMessage && (
          <Banner variant="error" inline description={errorMessage} closable onClose={() => setErrorMessage(null)} />
        )}

        <div className="py-2">
          <AvatarDetails
            title={displayName}
            text={
              <div className="flex flex-col">
                {employer.primaryFacility?.name && (
                  <div className="text-body-small text-sys-brand-on-surface-variant">
                    {employer.primaryFacility.name}
                  </div>
                )}
                {employer.email && (
                  <div className="text-body-small text-sys-brand-on-surface-variant">{employer.email}</div>
                )}
              </div>
            }
            picture={employer.profilePhoto}
          />
        </div>

        <Banner
          variant="info"
          inline
          description='People with "Manage Jobs" role can post jobs on the pga.org job board and view job applicants.'
        />
      </div>
    </Dialog>
  );
}
